import { memo, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useAtomValue } from 'jotai/react';
import { contentTransformAtom } from '@user/pages/ViewModelPage/contentTransformAtom';
import { NewElementStyleProviderContext } from '@view-model/models/sticky/user-context/NewElementStyleProvider/NewElementStyleProviderContext';
import { NodeStyle, StickyNode } from '@view-model/models/sticky/StickyNodeView';
import type { ThemeColor } from '@view-model/models/common/color';

type Props = {
  position: { x: number; y: number } | null;
  /** 指定された場合はこの色でゴーストを描画する。省略時は NewElementStyleProvider の現在色を使用する。 */
  color?: ThemeColor;
};

/**
 * ノードのドラッグ中に表示するゴースト。
 * 実際のノードの見た目（背景色・枠線色・サイズ）を現在のズームスケールに合わせて描画する。
 */
const NodeDragGhostFn: React.FC<Props> = ({ position, color }: Props) => {
  const contentTransform = useAtomValue(contentTransformAtom);
  const styleProvider = useContext(NewElementStyleProviderContext);

  if (!position) return null;

  const { width: canvasWidth, height: canvasHeight } = StickyNode.size();
  const scale = contentTransform.k;
  const screenWidth = canvasWidth * scale;
  const screenHeight = canvasHeight * scale;
  const { fillColor, borderColor } = color ? NodeStyle.getStyles(color) : styleProvider.nodeStyle().getStyles();

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: screenWidth,
        height: screenHeight,
        // box-sizing: border-box にすることで border 分が width/height 内に収まり、
        // translate(-50%, -50%) によるカーソル中央揃えが border の影響を受けない
        boxSizing: 'border-box',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: 9999,
        backgroundColor: fillColor,
        border: `2px solid ${borderColor}`,
        borderRadius: 4,
        opacity: 0.6,
      }}
    />,
    document.body
  );
};

export const NodeDragGhost = memo(NodeDragGhostFn);
